import { firstValueFrom } from 'rxjs';
import { ComponentFactoryResolver, Directive } from '@angular/core';
import {
  AddressLocationManager,
  CieUtilsModalManagerService,
  IDeliveryOrder,
  PickupCalendarDay,
  ToDoorCalendarDay
} from '@cakeiteasy/web-utils';
import { IDeliveryAddress } from '../users';
import { getEmptyToDoorLocation, isGeoLocationValid, mapDeliveryToLookupAddress } from '../delivery/delivery-helper';
import {
  ChooseDeliveryAddressModalComponent
} from '../delivery/components/choose-delivery-address-modal/choose-delivery-address-modal.component';
import { DeliveryFormBase } from './delivery-form.base';

@Directive({ standalone: false })
export abstract class LookupAddressDeliveryBase extends DeliveryFormBase {
  busyAddress: Promise<any>;
  lookupAddress = '';
  geoLocationStatus = '';
  deliveryError: { title?: string; message?: string } | null = null;
  maxDate = null;

  protected constructor(protected addressLocation: AddressLocationManager,
                        protected modalManager: CieUtilsModalManagerService,
                        protected componentResolver: ComponentFactoryResolver) {
    super();
  }

  abstract onAddressSelected(address: string): Promise<void>;

  abstract scheduleChange(calendarDay: ToDoorCalendarDay | PickupCalendarDay): Promise<void>;

  resetError() {
    this.deliveryError = null;
  }

  resetDeliveryState() {
    this.resetError();
    this.deliveryForm.patchValue(getEmptyToDoorLocation());
    this.geoLocationStatus = '';
  }

  async initByFavoriteAddress(deliveryAddresses: IDeliveryAddress[]): Promise<void> {
    const address = this.getInitialAddress(deliveryAddresses);

    if (!address) {
      return;
    }

    this.lookupAddress = mapDeliveryToLookupAddress(address);
    await this.onAddressSelected(this.lookupAddress);
  }

  async deliveryChecker(deliveryAddress: IDeliveryAddress): Promise<boolean> {
    const locationAddress = mapDeliveryToLookupAddress(deliveryAddress);
    const geoLocation = await firstValueFrom(this.addressLocation.getLocationInfo(locationAddress, this.countryCode));

    return isGeoLocationValid(geoLocation);
  }

  async chooseDeliveryAddress(): Promise<void> {
    const data = {
      deliveryAddresses: this.deliveryAddresses,
      deliveryChecker: this.deliveryChecker.bind(this)
    };

    try {
      const deliveryAddress: IDeliveryAddress = await this.modalManager
        .show(ChooseDeliveryAddressModalComponent, this.componentResolver, data, { windowClass: 'modal-simple lg' });

      const { recipient: deliveryUserName, phone: deliveryPhone , note: deliveryNote } = deliveryAddress;
      this.deliveryForm.patchValue({
        deliveryUserName,
        deliveryPhone,
        ...(deliveryNote && { deliveryNote }),
      });
      this.lookupAddress = mapDeliveryToLookupAddress(deliveryAddress);
      await this.onAddressSelected(this.lookupAddress);
    } catch (e) {
      console.log('User chosen nothing');
    }
  }

  protected async initLookupAddress(deliveryOrder: Partial<IDeliveryOrder>) {
    const { deliveryCity, deliveryAddress, deliveryPostNumber } = deliveryOrder;
    const postNumberCity = [deliveryPostNumber, deliveryCity].filter(Boolean).join(' ');
    this.lookupAddress = [deliveryAddress, postNumberCity].join(', ');
    await this.onAddressSelected(this.lookupAddress);
  }
}
